import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Trash2 } from 'lucide-react'
import type { ChatMessageResponse, ChatParticipant } from '@/types/teacher-chat'
import { teacherChatApi } from '@/services/api/teacherChat'
import { getInitials, formatTime } from '@/utils/chatHelpers'
import { useToast } from '@/components/ui/ToastProvider'
import { lessonShortLabel } from './scopeUtils'

interface ChatMessageBubbleProps {
  message: ChatMessageResponse
  participant?: ChatParticipant
  isOwn: boolean
  requestId: string
  canDelete: boolean
}

export function ChatMessageBubble({ message, participant, isOwn, requestId, canDelete }: ChatMessageBubbleProps) {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  const [deleting, setDeleting] = useState(false)
  const color = participant?.color || '#6b7280'

  const handleDelete = async () => {
    if (!confirm('Nachricht wirklich löschen?')) return
    setDeleting(true)
    try {
      await teacherChatApi.deleteMessage(requestId, message.id)
      await queryClient.invalidateQueries({ queryKey: ['chat-messages', requestId] })
      showToast({ message: 'Nachricht gelöscht', type: 'success', duration: 3000 })
    } catch {
      showToast({ message: 'Nachricht konnte nicht gelöscht werden', type: 'error', duration: 5000 })
    } finally {
      setDeleting(false)
    }
  }

  const displayName = isOwn ? 'Ich' : message.sender_name

  const scopeLessons = message.lessons
  const scopeBadge = scopeLessons && scopeLessons.length > 0 ? (
    <div className={`mt-1 flex items-center gap-1 flex-wrap ${isOwn ? 'justify-end' : ''}`}>
      {scopeLessons.length === 1 ? (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-100">
          {lessonShortLabel(scopeLessons[0]!)}
        </span>
      ) : (
        <span
          className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-indigo-50 text-indigo-600 border border-indigo-100 cursor-help"
          title={scopeLessons.map(lessonShortLabel).join('\n')}
        >
          {scopeLessons.length} Lektionen
        </span>
      )}
    </div>
  ) : null

  if (isOwn) {
    return (
      <div className="flex gap-3 max-w-lg ml-auto flex-row-reverse group">
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
          style={{ backgroundColor: color }}
          title={message.sender_name}
        >
          {getInitials(message.sender_name)}
        </div>
        <div className="min-w-0">
          <div className="text-[10px] text-gray-400 mb-1 text-right">
            {displayName} · {formatTime(message.created_at)}
          </div>
          <div className="bg-blue-600 text-white rounded-lg rounded-tr-none p-3 text-sm shadow-sm whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
            {message.body}
          </div>
          {scopeBadge}
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="opacity-0 group-hover:opacity-100 text-[10px] text-red-500 mt-1 flex items-center gap-1 ml-auto transition-opacity disabled:opacity-50"
              aria-label="Nachricht löschen"
            >
              <Trash2 className="w-3 h-3" /> Löschen
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="flex gap-3 max-w-lg group">
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-[10px] font-bold shrink-0"
        style={{ backgroundColor: color }}
        title={message.sender_name}
      >
        {getInitials(message.sender_name)}
      </div>
      <div className="min-w-0">
        <div className="text-[10px] text-gray-400 mb-1">
          {displayName} · {formatTime(message.created_at)}
        </div>
        <div className="bg-white border border-gray-200 rounded-lg rounded-tl-none p-3 text-sm text-gray-700 shadow-sm whitespace-pre-wrap break-words max-h-80 overflow-y-auto">
          {message.body}
        </div>
        {scopeBadge}
        {canDelete && (
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="opacity-0 group-hover:opacity-100 text-[10px] text-red-500 mt-1 flex items-center gap-1 transition-opacity disabled:opacity-50"
            aria-label="Nachricht löschen"
          >
            <Trash2 className="w-3 h-3" /> Löschen
          </button>
        )}
      </div>
    </div>
  )
}
